import {
  ReactNode,
  Fragment,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useNavigate } from "@tanstack/react-router";
import type { FunctionReference } from "convex/server";
import { Edit, Eye, Trash, type LucideIcon } from "lucide-react";
import { useTableData } from "./useTableData";
import { useTableNavigation } from "./table/useTableNavigation";
import { useModal, useDeleteModal } from "./useModal";
import { usePermissions } from "@/hooks/usePermissions";
import { useCreateShortcut } from "@/hooks/useCreateShortcut";
import { useAIContext } from "@/components/context/ai-context";

import { Filter, HeaderAction } from "@/components/table/table-filters";
export type { HeaderAction } from "@/components/table/table-filters";
import DataTable, { type BulkAction } from "@/components/table/data-table";
export type { BulkAction } from "@/components/table/data-table";
import { TableFilters } from "@/components/table/table-filters";
import OfflineDataNotice from "@/components/offline/offline-data-notice";
import { isOffline } from "@/lib/offline/is-offline";
import { DeleteConfirmationModal } from "@/components/ui/modal/delete-confirmation-modal";
import { TableColumnGeneric } from "./table/types";
import { IconName } from "@/lib/modules";
import { modulesTypes } from "@/convex/system/permissions";

export interface TableModalProps<TData> {
  isOpen: boolean;
  data?: TData;
  onClose: () => void;
  onSuccess: () => void;
}

export interface TableConfig<TData> {
  tableName: typeof modulesTypes.type;
  query: FunctionReference<"query">;
  title: string | (() => string);
  icon?: IconName;
  /** Icono del encabezado que gana sobre el del módulo (ver DataTable). */
  headerIcon?: () => string | undefined;
  description: string | (() => string);
  /**
   * Columnas que el factory debe pasar a `DataTable` con el flag
   * `hiddenByDefault`. Sirve para columnas virtuales que no
   * forman parte del catálogo configurable del usuario (ej. UUID)
   * y deben arrancar ocultas la primera vez, pero pueden
   * activarse desde el menú de columnas.
   */
  hiddenByDefault?: ReadonlySet<string>;
  /**
   * Override reactivo de visibilidad por columna (server como fuente
   * de verdad para los system fields virtuales de `collection_records`).
   * Si se define, tiene precedencia sobre `localStorage` y
   * `hiddenByDefault`. El factory inyecta esto desde el render del
   * `DataTable`; ver `hooks/useColumnVisibility.ts`.
   */
  columnVisibilityOverride?: Readonly<Record<string, boolean>>;
  /** `customHooksData`: lo que devuelve `customHooks` (p. ej. datos por fila). */
  columns: (
    helpers: TableHelpers<TData>,
    customHooksData: Record<string, any>,
  ) => TableColumnGeneric<TData>[];
  filters?: (
    helpers: TableHelpers<TData>,
    customHooksData: Record<string, unknown>,
  ) => Filter[];
  actions?: (
    helpers: TableActionHelpers<TData>,
    customHooksData: Record<string, unknown>,
  ) => TableActions<TData>;
  modal?: {
    component?: React.ComponentType<TableModalProps<TData>>;
    getItemName?: (item: TData) => string;
  };
  customHooks?: (
    data: TData[],
    helpers: TableHelpers<TData>,
  ) => Record<string, any>;
  /**
   * Override del mapeo de (currentPage, pageSize, searchParams) → queryArgs.
   * Útil cuando la query usa `paginationOptsValidator` u otro shape distinto
   * al default (`{ page, targetMaxRows, includeTotalCount, search, status, ...rest }`).
   */
  buildQueryParams?: (
    currentPage: number,
    pageSize: number,
    searchParams: Record<string, any>,
  ) => Record<string, any>;
  /**
   * Override del armado de URLs de paginación. Default preserva search/status/page
   * + targetMaxRows. Usar cuando la paginación depende de filtros extra.
   */
  buildPageUrl?: (
    basePath: string,
    page: number,
    searchParams: Record<string, any>,
  ) => string;
  createAction?: "modal" | "route" | "none";
  createRoute?: string;
  /**
   * Atajo de teclado para crear un registro (tecla "n" por defecto). Se activa
   * cuando createAction no es "none" y el usuario tiene permiso de crear. Poné
   * `false` para desactivarlo en una tabla puntual. Ver `useCreateShortcut`.
   */
  createShortcut?: boolean;
  /**
   * Muestra la barra superior (buscador, agregar, filas) aunque la tabla no
   * declare filtros. Por defecto la barra solo aparece con filtros.
   */
  showToolbar?: boolean;
  /**
   * Envuelve la barra (buscador, filtros, acciones) antes de pintarla: p. ej.
   * las colecciones la mandan a la fila de sus pestañas de vistas.
   */
  wrapToolbar?: (toolbar: React.ReactNode) => React.ReactNode;
  /** Buscador propio en lugar del campo de la barra (ver `TableFilters`). */
  renderSearch?: (
    props: { value: string; onSearch: (value: string) => void },
    customHooksData: Record<string, unknown>,
  ) => React.ReactNode;
  headerActions?: (customHooksData: Record<string, unknown>) => HeaderAction[];
  enableRowSelection?: boolean;
  /** Habilita reordenar columnas por drag & drop (grip en el header). */
  enableColumnReorder?: boolean;
  /** Habilita pinnear (congelar a la izquierda) columnas desde el header. */
  enableColumnPin?: boolean;
  /** Habilita el botón de agregaciones (sum/avg/count/min/max) en el header. */
  enableAggregations?: boolean;
  onSelectionChange?: (selected: Set<string>) => void;
  bulkActions?: (
    helpers: TableActionHelpers<TData>,
    customHooksData: Record<string, unknown>,
  ) => BulkAction<TData>[];
  bulkActionsLabel?: string;
  /**
   * Filas "extra" inyectadas desde el cliente (no vienen de la query de Convex).
   * Sirve, por ejemplo, para mostrar borradores locales sin materializar como
   * filas de la tabla. Es un HOOK: se invoca en render igual que `columns` /
   * `actions`, así que puede usar otros hooks (useNavigate, lecturas de
   * localStorage reactivas, etc.). Las filas extra:
   *  - se anteponen a las del servidor SOLO en la página 1 (para no repetirlas
   *    al paginar ni ensuciar el `totalCount`),
   *  - usan `rowActions` propias (acciones limitadas) en vez de las estándar,
   *  - no son seleccionables (no entran al bulk que opera sobre ids del server).
   */
  useExtraRows?: () => {
    rows: TData[];
    rowActions: (row: TData) => Array<{
      id: string;
      label: string;
      icon: ReactNode;
      onClick: (row: TData) => void;
      variant?:
        | "default"
        | "destructive"
        | "outline"
        | "secondary"
        | "ghost"
        | "link";
    }>;
    isExtraRow: (row: TData) => boolean;
    rowClassName?: (row: TData) => string | undefined;
    /**
     * Predicado para ocultar filas del SERVER (dedupe durante una
     * sincronización: la fila local ya muestra el contenido, así que se esconde
     * su contraparte todavía a medio materializar hasta que el commit la
     * complete). Se evalúa sobre el campo propio de la fila (no un estado
     * cliente aparte), así que no hay carrera de render.
     */
    hideServerRow?: (row: TData) => boolean;
  };
  /**
   * Hook opcional para fuente de verdad server-side de la visibilidad
   * de columnas. Se invoca en render (igual que `useExtraRows`)
   * después de construir `columns` — recibe las columnas ya
   * resueltas para que pueda derivar `allColumnKeys`. Devuelve el
   * `ExternalColumnVisibility` que se inyecta al `DataTable`. Si se
   * omite, el factory cae al comportamiento histórico
   * (`localStorage` + `hiddenByDefault` +
   * `columnVisibilityOverride`).
   */
  useServerColumnVisibility?: (
    columns: TableColumnGeneric<TData>[],
  ) =>
    | import("@/hooks/useColumnVisibility").ExternalColumnVisibility
    | undefined;
}

export interface TableHelpers<TData> {
  navigate: ReturnType<typeof useNavigate>;
  formatCurrency: (amount: number, currency?: string) => string;
  formatDate: (date: number | Date) => string;
}

export interface TableActionHelpers<TData> extends TableHelpers<TData> {
  modal: ReturnType<typeof useModal<TData>>;
  deleteModal: ReturnType<typeof useDeleteModal<TData>>;
  can: (
    action: "read" | "create" | "update" | "delete",
    module?: string,
  ) => boolean;
}

export interface TableActions<TData> {
  onView?: (item: TData) => void;
  onEdit?: (item: TData) => void;
  onDelete?: (
    item: TData,
    options?: { skipModal?: boolean },
  ) => void | Promise<void>;
  deleteCondition?: (item: TData) => boolean;
  /** Borrar funciona sin conexión (colección disponible sin conexión: va a la outbox). */
  deleteAvailableOffline?: boolean;
  customActions?: Array<{
    label: string;
    icon: LucideIcon;
    onClick: (item: TData) => void;
    variant?: "default" | "ghost" | "outline";
    condition?: (item: TData) => boolean;
    permission?: "read" | "create" | "update" | "delete";
    /** true = la acción funciona sin conexión (no se deshabilita offline).
     *  Por defecto, las custom actions (mutaciones) se deshabilitan offline. */
    availableOffline?: boolean;
  }>;
}

/**
 * Parámetros de búsqueda base que son comunes en todas las tablas.
 */
export interface BaseSearchParams {
  page?: number;
  search?: string;
  status?: string;
}

export interface TableProps<
  TSearchParams extends Record<string, any> = BaseSearchParams,
> {
  currentPage: number;
  pageSize?: number;
  basePath: string;
  searchParams?: TSearchParams;
}

export interface UseTableOptions {
  currentPage: number;
  pageSize?: number;
  basePath: string;
  searchParams?: Record<string, any>;
  /**
   * Args extra que se mergean **encima** del resultado de
   * `buildQueryParams` antes de invocar la query de Convex.
   * Útil cuando el componente que monta el factory necesita
   * pasar argumentos reactivos (ej. `valueFilters` calculado
   * en el render del page) sin tener que reorganizar el
   * factory.
   */
  queryArgs?: Record<string, any>;
  /**
   * Override reactivo de visibilidad por columna (server como
   * fuente de verdad). Tiene precedencia sobre `localStorage` y
   * `hiddenByDefault`. Si se omite, el factory cae al
   * `columnVisibilityOverride` estático del config o a nada.
   */
  columnVisibilityOverride?: Readonly<Record<string, boolean>>;
  /**
   * Modo "server-side" de visibilidad. Cuando se pasa, el factory
   * deja de usar `localStorage` y delega al caller la persistencia.
   * Útil en `collection_records` con `useCollectionColumnVisibility`.
   */
  externalColumnVisibility?: import("@/hooks/useColumnVisibility").ExternalColumnVisibility;
  /**
   * Id de la tabla para lo que se guarda en el navegador (orden, fijado,
   * anchos, orden de filas, vista). Por defecto `${tableName}-table`. Las
   * tablas que muestran datos distintos con el mismo `tableName` (una por
   * coleccion) pasan el suyo para no compartir preferencias.
   */
  tableId?: string;
  /**
   * Mantiene el esqueleto del CUERPO de la tabla aunque los datos ya hayan
   * llegado (p. ej. mientras cargan las columnas de una coleccion). El resto
   * de la tabla (titulo, barra, cabecera) se pinta igual.
   */
  forceLoading?: boolean;
  /**
   * `false` oculta "Agregar" y desactiva el atajo "n" aunque el rol tenga el
   * permiso del modulo (p. ej. una coleccion que restringe quien crea).
   * Ausente = solo el permiso del modulo.
   */
  canCreate?: boolean;
  /**
   * Las columnas aun no estan listas (dependen de otra consulta): el
   * esqueleto muestra las cabeceras de la ultima visita a `tableId`.
   */
  columnsPending?: boolean;
}

export interface UseTableReturn<TData> {
  data: TData[];
  isLoading: boolean;
  isError: boolean;
  currentPage: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  modal: ReturnType<typeof useModal<TData>>;
  deleteModal: ReturnType<typeof useDeleteModal<TData>>;
  customHooksData: Record<string, any>;
  selectedRows: Set<string>;
  clearSelection: () => void;
  renderTable: () => ReactNode;
  renderModals: () => ReactNode;
  render: (options?: { extraContent?: ReactNode }) => ReactNode;
}

// ─── Stable formatters (module-level, no re-creation) ────────────────────────
const currencyFormatters = new Map<string, Intl.NumberFormat>();
function formatCurrencyStable(amount: number, currency = "HNL"): string {
  let fmt = currencyFormatters.get(currency);
  if (!fmt) {
    fmt = new Intl.NumberFormat("es-ES", { style: "currency", currency });
    currencyFormatters.set(currency, fmt);
  }
  return fmt.format(amount);
}

const dateFormatter = new Intl.DateTimeFormat("es-ES");
function formatDateStable(date: number | Date): string {
  return dateFormatter.format(typeof date === "number" ? new Date(date) : date);
}

export function createTableHook<TData extends Record<string, any>>(
  config: TableConfig<TData>,
) {
  return function useTable(options: UseTableOptions): UseTableReturn<TData> {
    const navigate = useNavigate();
    const { can } = usePermissions(config.tableName as any);
    const { setContext, removeContext } = useAIContext();

    // Fetch data
    const tableData = useTableData<TData>(
      {
        tableName: config.tableName,
        query: config.query,
        title:
          typeof config.title === "function" ? config.title() : config.title,
        description:
          typeof config.description === "function"
            ? config.description()
            : config.description,
        buildQueryParams: config.buildQueryParams as any,
        buildPageUrl: config.buildPageUrl as any,
      },
      {
        currentPage: options.currentPage,
        pageSize: options.pageSize,
        basePath: options.basePath,
        searchParams: options.searchParams,
        extraArgs: options.queryArgs,
      },
    );

    // Navigation
    const { navigateToPage, updateFilters, isPending } = useTableNavigation({
      basePath: options.basePath,
      currentPage: tableData.currentPage,
      nextPageUrl: tableData.nextPageUrl,
      previousPageUrl: tableData.previousPageUrl,
      resetFiltersUrl: tableData.resetFiltersUrl,
    });

    // Modals
    const modal = useModal<TData>();
    const deleteModal = useDeleteModal<TData>(
      config.modal?.getItemName ||
        ((item: TData) => {
          const r = item as Record<string, unknown>;
          return (
            (r?.code as string) ||
            (r?.name as string) ||
            (r?._id as string) ||
            "item"
          );
        }),
    );

    // ─── Atajo de teclado "n" → crear registro ────────────────────────────────
    // Dispara la acción de creación (modal o ruta) si el usuario tiene permiso.
    useCreateShortcut(
      () => {
        if (config.createAction === "route") {
          if (config.createRoute) navigate({ to: config.createRoute });
          return;
        }
        // Por defecto (createAction "modal" o sin definir) usamos el modal.
        modal.create();
      },
      config.createShortcut !== false &&
        config.createAction !== "none" &&
        options.canCreate !== false &&
        can("create"),
    );

    // ─── Stable helpers (navigate changes identity, but formatters are stable) ──
    const helpers: TableHelpers<TData> = useMemo(
      () => ({
        navigate,
        formatCurrency: formatCurrencyStable,
        formatDate: formatDateStable,
      }),
      [navigate],
    );

    // ─── Action helpers (stable while deps are stable) ───────────────────────
    const actionHelpers: TableActionHelpers<TData> = useMemo(
      () => ({
        ...helpers,
        modal,
        deleteModal,
        can: (action, module) => can(action, module as any),
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [helpers, modal, deleteModal, can],
    );

    // ─── Custom hooks ─────────────────────────────────────────────────────────
    const customHooksData: Record<string, any> = config.customHooks
      ? config.customHooks(tableData.data || [], helpers)
      : {};

    // ─── Derived config values ────────────────────────────────────────────────
    // config.columns / config.filters / config.actions may all call hooks
    // (useTranslation, useNavigate, useToastMutation, etc.) so they MUST be
    // called directly during render — never inside useMemo/useCallback.
    const columns = config.columns(helpers, customHooksData);

    // config.filters may call hooks (useTranslation, etc.)
    // — must be called directly during render, never inside useMemo/useCallback
    const filters = config.filters
      ? config.filters(helpers, customHooksData)
      : [];

    // config.actions can call hooks (useNavigate, useToastMutation, etc.)
    // — must be called directly during render, never inside useMemo/useCallback
    const tableActions = config.actions
      ? config.actions(actionHelpers, customHooksData)
      : {};

    // Collect any React bars/portals returned alongside actions
    const actionBars: ReactNode[] = Array.isArray((tableActions as any).bars)
      ? (tableActions as any).bars
      : [];

    // config.headerActions may also call hooks
    const headerActions: HeaderAction[] = config.headerActions
      ? config.headerActions(customHooksData)
      : [];

    const toolbarNode = () => (
<TableFilters
              table={config.tableName}
              tableName={titleStr}
              filters={filters}
              updateFilters={updateFilters}
              setIsModalOpen={
                config.createAction === "modal" && options.canCreate !== false ? modal.create : undefined
              }
              rootPath={options.basePath}
              createRoute={config.createRoute}
              defaultPageSize={tableData.pageSize}
              headerActions={headerActions}
              renderSearch={config.renderSearch ? (props) => config.renderSearch!(props, customHooksData) : undefined}
              createShortcut={
                config.createShortcut === false ||
                config.createAction === "none" ||
                options.canCreate === false
                  ? undefined
                  : "N"
              }
            />
    );

    // config.bulkActions may also call hooks
    const bulkActions: BulkAction<TData>[] = config.bulkActions
      ? config.bulkActions(actionHelpers, customHooksData)
      : [];

    // config.useExtraRows is a hook — call it unconditionally (its presence is
    // static per config, so hooks order is stable). Merge only on page 1.
    const extraRows = config.useExtraRows?.();

    // Server-side column visibility (opcional). Si el config lo
    // declara, lo invocamos una sola vez al render (recibe `columns`
    // ya construido) y mergeamos con cualquier override pasado por el
    // caller (server-override manda).
    const configServerVisibility = config.useServerColumnVisibility?.(columns);
    /**
     * Merge del external visibility entre lo que pasa el caller
     * (`options.externalColumnVisibility`) y lo que expone el
     * config (`configServerVisibility`). Server-override manda.
     *
     * Dependemos de los CAMPOS, no del objeto entero: el hook
     * del config (`useCollectionColumnVisibility` u otros)
     * devuelve un objeto literal cada render con callbacks
     * nuevos, lo cual haría que `useMemo` re-ejecute siempre.
     * Estabilizando por campo evitamos re-renders innecesarios
     * en `useColumnVisibility` y, por extensión, en todo el
     * `DataTable`.
     */
    const mergedExternalVisibility = useMemo<
      import("@/hooks/useColumnVisibility").ExternalColumnVisibility | undefined
    >(() => {
      const next =
        options.externalColumnVisibility ?? configServerVisibility;
      return next;
    }, [
      configServerVisibility?.externalVisible,
      configServerVisibility?.onVisibilityChange,
      options.externalColumnVisibility?.externalVisible,
      options.externalColumnVisibility?.onVisibilityChange,
    ]);

    // ─── Delete state ─────────────────────────────────────────────────────────
    const [deleteInProgress, setDeleteInProgress] = useState(false);

    // ─── Row selection ────────────────────────────────────────────────────────
    const [internalSelectedRows, setInternalSelectedRows] = useState<
      Set<string>
    >(new Set());

    // Assign ref directly during render — no useEffect needed (React 19 pattern)
    const tableDataRef = useRef<any[]>([]);
    tableDataRef.current = tableData.data || [];

    const handleSelectionChange = useCallback(
      (newSelection: Set<string | number>) => {
        const stringSet = new Set(
          Array.from(newSelection).map((v) => String(v)),
        );

        queueMicrotask(() => {
          if (stringSet.size > 0) {
            const loadedData = tableDataRef.current.filter((row: any) =>
              stringSet.has(row._id),
            );
            setContext({
              id: `table-${config.tableName}`,
              type: "selection",
              // config.title / config.tableName are module-level static values
              label: `${stringSet.size} ${typeof config.title === "function" ? config.title() : config.title} seleccionado${stringSet.size > 1 ? "s" : ""}`,
              data: {
                table: config.tableName,
                ids: Array.from(stringSet),
                rows: loadedData,
              },
            });
          } else {
            removeContext(`table-${config.tableName}`);
          }

          if (config.onSelectionChange) {
            config.onSelectionChange(stringSet);
          } else {
            setInternalSelectedRows(stringSet);
          }
        });
      },
      // config.* are defined at module level — static, safe to omit from deps
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [setContext, removeContext],
    );

    const clearSelection = useCallback(
      () => handleSelectionChange(new Set()),
      [handleSelectionChange],
    );

    // ─── Row actions (memoized builder) ──────────────────────────────────────
    const getRowActions = useCallback(
      (item: TData) => {
        // Offline: las acciones que son mutaciones (eliminar, y las custom que no
        // se marquen `availableOffline`) se deshabilitan — fallarían igual sin
        // conexión. Ver/Editar (navegación) quedan disponibles.
        const offline = isOffline();
        const baseActions: Array<{
          id: string;
          label: string;
          icon: ReactNode;
          onClick: (row: TData) => void;
          disabled?: boolean;
          variant?:
            | "default"
            | "destructive"
            | "outline"
            | "secondary"
            | "ghost"
            | "link";
        }> = [];

        if (tableActions.onView && can("read")) {
          baseActions.push({
            id: "view",
            label: "Ver",
            icon: <Eye className="p-px" />,
            onClick: () => tableActions.onView!(item),
          });
        }

        if (tableActions.onEdit && can("update")) {
          baseActions.push({
            id: "edit",
            label: "Editar",
            icon: <Edit className="p-px" />,
            onClick: () => tableActions.onEdit!(item),
          });
        }

        if (tableActions.customActions) {
          tableActions.customActions.forEach((action, index) => {
            if (action.condition && !action.condition(item)) return;
            if (action.permission && !can(action.permission)) return;

            baseActions.push({
              id: `custom-${index}`,
              label: action.label,
              icon: action.icon ? <action.icon className="p-px" /> : null,
              onClick: () => action.onClick(item),
              variant: action.variant,
              disabled: offline && !action.availableOffline,
            });
          });
        }

        const canDelete =
          tableActions.onDelete &&
          can("delete") &&
          (!tableActions.deleteCondition || tableActions.deleteCondition(item));

        if (canDelete) {
          baseActions.push({
            id: "delete",
            label: "Eliminar",
            icon: <Trash className="p-px" />,
            disabled: offline && !tableActions.deleteAvailableOffline,
            onClick: () => {
              if ((offline && !tableActions.deleteAvailableOffline) || deleteInProgress) return;
              setDeleteInProgress(true);
              const result = tableActions.onDelete!(item);
              if (result instanceof Promise) {
                result.finally(() => setDeleteInProgress(false));
              } else {
                const checkModal = () => {
                  if (!deleteModal.isOpen) {
                    setDeleteInProgress(false);
                  } else {
                    setTimeout(checkModal, 100);
                  }
                };
                setTimeout(checkModal, 100);
              }
            },
            variant: "destructive" as const,
          });
        }

        return baseActions;
      },
      [tableActions, can, deleteInProgress, deleteModal.isOpen],
    );

    // ─── Render helpers ───────────────────────────────────────────────────────
    const titleStr =
      typeof config.title === "function" ? config.title() : config.title;
    const descStr =
      typeof config.description === "function"
        ? config.description()
        : config.description;

    // Mostramos filas locales + de réplica/server JUNTAS, nunca en dos pasos.
    // "Listo" = ya llegó el server/réplica (data !== undefined), O —offline— la
    // réplica local terminó de hidratar (con o sin filas). Compartido entre el
    // render de filas y el conteo del footer para que no se desincronicen.
    const showExtraRows = useMemo(
      () =>
        !!extraRows &&
        tableData.currentPage === 1 &&
        extraRows.rows.length > 0 &&
        (tableData.data !== undefined ||
          (isOffline() && tableData.replicaHydrated)),
      [extraRows, tableData.currentPage, tableData.data, tableData.replicaHydrated],
    );
    const extraRowsCount = showExtraRows ? extraRows!.rows.length : 0;

    const renderTable = useCallback(
      () => (
        <div className="space-y-2">
          {(filters.length > 0 || config.showToolbar) &&
            (config.wrapToolbar ? config.wrapToolbar(toolbarNode()) : toolbarNode())}
          {tableData.isOfflineNoData &&
          !(
            extraRows &&
            tableData.currentPage === 1 &&
            extraRows.rows.length > 0
          ) ? (
            <OfflineDataNotice />
          ) : (
          <DataTable<TData>
            title={titleStr}
            icon={config.icon}
            headerIcon={config.headerIcon?.()}
            description={descStr}
            columns={columns}
            rowActions={(row: TData) =>
              extraRows?.isExtraRow(row)
                ? extraRows.rowActions(row)
                : getRowActions(row)
            }
            data={(() => {
              const serverRows = tableData.data || [];
              if (!extraRows) return serverRows;
              const hide = extraRows.hideServerRow;
              const visibleServer = hide
                ? serverRows.filter((r) => !hide(r))
                : serverRows;
              return showExtraRows
                ? [...extraRows.rows, ...visibleServer]
                : visibleServer;
            })()}
            rowClassName={extraRows?.rowClassName}
            isRowSelectable={
              extraRows ? (row: TData) => !extraRows.isExtraRow(row) : undefined
            }
            columnsPending={options.columnsPending}
            isLoading={
              Boolean(options.forceLoading) ||
              ((tableData.isLoading || isPending) &&
              !tableData.isOfflineNoData &&
              // Mantenemos el skeleton hasta estar "listos" (server/réplica), así
              // las filas locales y las de réplica aparecen a la vez. Sin
              // extraRows, el skeleton sigue la lógica normal de carga.
              !(
                extraRows &&
                (tableData.data !== undefined ||
                  (isOffline() && tableData.replicaHydrated))
              ))
            }
            pagination={{
              totalCount: tableData.totalCount + extraRowsCount,
              totalCountIsLowerBound: tableData.totalCountIsLowerBound,
              currentPage: tableData.currentPage,
              onPageChange: navigateToPage,
              pageSize: tableData.pageSize,
            }}
            emptyState={{
              title: `No hay ${titleStr.toLowerCase()}`,
              description: `Comienza creando ${titleStr.toLowerCase()}.`,
            }}
            enableColumnVisibility={true}
            hiddenByDefault={config.hiddenByDefault}
            columnVisibilityOverride={
              options.columnVisibilityOverride ?? config.columnVisibilityOverride
            }
            externalColumnVisibility={mergedExternalVisibility}
            enableColumnReorder={config.enableColumnReorder ?? false}
            enableColumnPin={config.enableColumnPin ?? false}
            enableAggregations={config.enableAggregations ?? false}
            tableId={options.tableId ?? `${config.tableName}-table`}
            rowIdField="_id"
            enableRowSelection={config.enableRowSelection}
            selectedRows={internalSelectedRows}
            onSelectionChange={handleSelectionChange}
            bulkActions={bulkActions.length > 0 ? bulkActions : undefined}
            bulkActionsLabel={config.bulkActionsLabel}
            offlineFromCache={tableData.isOfflineViewing}
          />
          )}
        </div>
      ),
      [
        filters,
        titleStr,
        descStr,
        columns,
        getRowActions,
        tableData,
        isPending,
        navigateToPage,
        updateFilters,
        modal.create,
        headerActions,
        bulkActions,
        internalSelectedRows,
        handleSelectionChange,
        options.basePath,
        extraRows,
        showExtraRows,
        extraRowsCount,
      ],
    );

    const renderModals = useCallback(() => {
      const ModalComponent = config.modal?.component;
      return (
        <>
          {ModalComponent && (
            <ModalComponent
              isOpen={modal.isOpen}
              data={modal.data}
              onClose={modal.close}
              onSuccess={modal.close}
            />
          )}
          <DeleteConfirmationModal
            isOpen={deleteModal.isOpen}
            isCritical={deleteModal.isCritical}
            itemName={
              deleteModal.data && config.modal?.getItemName
                ? config.modal.getItemName(deleteModal.data)
                : undefined
            }
            onClose={deleteModal.close}
            onConfirm={async () => {
              if (
                deleteModal.canConfirm &&
                deleteModal.data &&
                tableActions.onDelete
              ) {
                const result = tableActions.onDelete(deleteModal.data, {
                  skipModal: true,
                });
                if (result instanceof Promise) await result;
                setDeleteInProgress(false);
              }
              deleteModal.close();
            }}
            confirmationValue={deleteModal.confirmationValue}
            onConfirmationChange={deleteModal.setConfirmationValue}
            canConfirm={deleteModal.canConfirm}
          />
          {actionBars.map((bar, i) => (
            <Fragment key={i}>{bar}</Fragment>
          ))}
        </>
      );
    }, [modal, deleteModal, tableActions, actionBars]);

    const render = useCallback(
      (opts?: { extraContent?: ReactNode }) => (
        <>
          <div className="space-y-4">
            {renderTable()}
            {opts?.extraContent}
          </div>
          {/* Modales fuera del space-y para que no aporten un margen "fantasma"
              (mt-4) que sumaba alto al contenedor y desajustaba el scroll. */}
          {renderModals()}
        </>
      ),
      [renderTable, renderModals],
    );

    return {
      data: tableData.data || [],
      isLoading: tableData.isLoading,
      isError: tableData.isError,
      currentPage: tableData.currentPage,
      pageSize: tableData.pageSize,
      totalCount: tableData.totalCount,
      totalPages: tableData.totalPages,
      modal,
      deleteModal,
      selectedRows: internalSelectedRows,
      clearSelection,
      customHooksData,
      renderTable,
      renderModals,
      render,
    };
  };
}
